/**
 * Service Worker Registration and Management
 * Handles PWA functionality for offline caching and state preservation
 */

let swRegistration = null;
let isOnline = navigator.onLine;
let deferredPrompt = null;

/**
 * Initialize service worker and PWA features
 */
export async function initializeServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported in this browser');
    return false;
  }

  try {
    console.log('Registering Service Worker...');
    
    // Register the service worker
    swRegistration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    console.log('Service Worker registered successfully:', swRegistration.scope);

    // Handle service worker updates
    handleServiceWorkerUpdates();

    // Setup message handling
    setupMessageHandling();

    // Setup online/offline handling
    setupOnlineOfflineHandling();

    // Setup install prompt handling
    setupInstallPromptHandling();

    // Setup page visibility handling for iOS Safari
    setupPageVisibilityHandling();

    return true;

  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return false;
  }
}

/**
 * Handle service worker updates
 */
function handleServiceWorkerUpdates() {
  if (!swRegistration) return;

  swRegistration.addEventListener('updatefound', () => {
    const newWorker = swRegistration.installing;
    console.log('New Service Worker found, installing...');

    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        console.log('New Service Worker installed, showing update notification');
        showUpdateNotification();
      }
    });
  });

  // Check for updates periodically (skip when app is backgrounded to conserve battery)
  setInterval(() => {
    if (swRegistration && !document.hidden) {
      swRegistration.update();
    }
  }, 86400000); // Check every 24 hours
}

/**
 * Show update notification to user
 */
function showUpdateNotification() {
  const updateBanner = document.createElement('div');
  updateBanner.id = 'sw-update-banner';
  updateBanner.innerHTML = `
    <div class="update-banner">
      <div class="update-message">
        <span>🚀 A new version of Budgley is available!</span>
        <button class="update-btn" onclick="window.applyServiceWorkerUpdate()">Update Now</button>
        <button class="dismiss-btn" onclick="document.getElementById('sw-update-banner').remove()">Later</button>
      </div>
    </div>
  `;
  
  updateBanner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10000;
    background: #4CAF50;
    color: white;
    padding: 12px 16px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  `;

  document.body.appendChild(updateBanner);

  // Make update function globally available
  window.applyServiceWorkerUpdate = applyServiceWorkerUpdate;
}

/**
 * Apply service worker update
 */
function applyServiceWorkerUpdate() {
  if (!swRegistration || !swRegistration.waiting) return;

  // Tell the waiting service worker to become active
  swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });

  // Reload the page to use the new service worker
  window.location.reload();
}

/**
 * Setup message handling between service worker and app
 */
function setupMessageHandling() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('message', (event) => {
    const { type, data } = event.data;
    console.log('Message from Service Worker:', type, data);

    switch (type) {
      case 'SYNC_OFFLINE_DATA':
        handleOfflineDataSync();
        break;
        
      case 'CACHE_STATUS':
        console.log('Cache Status:', data);
        break;
        
      case 'VISIBILITY_CHANGED':
        handleVisibilityChange(data);
        break;
    }
  });
}

/**
 * Setup online/offline event handling
 */
function setupOnlineOfflineHandling() {
  window.addEventListener('online', () => {
    console.log('App went online');
    isOnline = true;
    updateOnlineStatus(true);
    triggerDataSync();
  });

  window.addEventListener('offline', () => {
    console.log('App went offline');
    isOnline = false;
    updateOnlineStatus(false);
  });
}

/**
 * Update online status indicator in UI
 */
function updateOnlineStatus(online) {
  // Remove any existing status indicators
  const existingIndicator = document.getElementById('network-status-indicator');
  if (existingIndicator) {
    existingIndicator.remove();
  }

  if (!online) {
    // Show offline indicator
    const offlineIndicator = document.createElement('div');
    offlineIndicator.id = 'network-status-indicator';
    offlineIndicator.innerHTML = `
      <div class="offline-banner">
        <span>📱 You're offline - changes will sync when connection is restored</span>
      </div>
    `;
    
    offlineIndicator.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 9999;
      background: #FF9800;
      color: white;
      padding: 8px 16px;
      text-align: center;
      font-size: 14px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    `;

    document.body.appendChild(offlineIndicator);
  }
}

/**
 * Setup install prompt handling for PWA
 */
function setupInstallPromptHandling() {
  window.addEventListener('beforeinstallprompt', (event) => {
    console.log('Install prompt ready');
    event.preventDefault();
    deferredPrompt = event;
    showInstallButton();
  });

  window.addEventListener('appinstalled', () => {
    console.log('PWA was installed');
    deferredPrompt = null;
    hideInstallButton();
  });
}

/**
 * Show install button for PWA
 */
function showInstallButton() {
  // Only show if not already installed
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return; // Already installed
  }

  const installButton = document.createElement('button');
  installButton.id = 'pwa-install-button';
  installButton.innerHTML = '📱 Install App';
  installButton.onclick = promptInstall;
  
  installButton.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
    background: #2196F3;
    color: white;
    border: none;
    padding: 12px 20px;
    border-radius: 25px;
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
  `;

  installButton.addEventListener('mouseover', () => {
    installButton.style.transform = 'translateY(-2px)';
    installButton.style.boxShadow = '0 6px 16px rgba(33, 150, 243, 0.4)';
  });

  installButton.addEventListener('mouseout', () => {
    installButton.style.transform = 'translateY(0)';
    installButton.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.3)';
  });

  document.body.appendChild(installButton);

  // Auto-hide after 10 seconds
  setTimeout(() => {
    if (installButton.parentNode) {
      installButton.style.opacity = '0.7';
    }
  }, 10000);
}

/**
 * Hide install button
 */
function hideInstallButton() {
  const installButton = document.getElementById('pwa-install-button');
  if (installButton) {
    installButton.remove();
  }
}

/**
 * Prompt user to install PWA
 */
async function promptInstall() {
  if (!deferredPrompt) return;

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  
  console.log('Install prompt outcome:', outcome);
  
  if (outcome === 'accepted') {
    console.log('User accepted the install prompt');
  }
  
  deferredPrompt = null;
  hideInstallButton();
}

/**
 * Setup page visibility handling for iOS Safari memory management
 */
function setupPageVisibilityHandling() {
  let appState = null;
  let saveStateTimer = null;

  function debouncedSaveState() {
    clearTimeout(saveStateTimer);
    saveStateTimer = setTimeout(() => {
      appState = captureAppState();
      saveStateToStorage(appState);
    }, 50);
  }

  // Save state and pause animations when page becomes hidden
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      console.log('Page hidden - saving state for iOS Safari');
      document.body.classList.add('app-hidden');
      debouncedSaveState();
    } else {
      console.log('Page visible - checking for state restoration');
      document.body.classList.remove('app-hidden');
      // Small delay to let the page fully restore
      setTimeout(() => {
        restoreAppStateIfNeeded();
      }, 100);
    }
  });

  // Handle iOS-specific events (pagehide/pageshow are reliable on iOS; blur is not)
  window.addEventListener('pagehide', (event) => {
    console.log('Page hide event - saving state', event.persisted);
    debouncedSaveState();
  });

  window.addEventListener('pageshow', (event) => {
    console.log('Page show event - restoring state', event.persisted);
    if (!event.persisted) {
      // Page was reloaded, restore state
      setTimeout(() => {
        restoreAppStateIfNeeded();
      }, 200);
    }
  });
}

/**
 * Capture current app state
 */
function captureAppState() {
  try {
    const state = {
      timestamp: Date.now(),
      url: window.location.href,
      scrollPosition: {
        x: window.scrollX,
        y: window.scrollY
      },
      activeView: getCurrentActiveView(),
      formData: captureFormData()
    };

    console.log('App state captured:', state);
    return state;
  } catch (error) {
    console.error('Failed to capture app state:', error);
    return null;
  }
}

/**
 * Get currently active view
 */
function getCurrentActiveView() {
  // Check which view is currently visible
  const views = ['single-mode', 'comparison-mode', 'sankey-mode', 'progress-view-mode', 'expense-tracker-mode'];
  
  for (const viewId of views) {
    const element = document.getElementById(viewId);
    if (element && element.style.display !== 'none') {
      return viewId;
    }
  }
  
  return 'single-mode'; // Default fallback
}

/**
 * Capture form data to preserve user inputs
 */
function captureFormData() {
  const formData = {};
  const forms = document.querySelectorAll('form');
  const inputs = document.querySelectorAll('input[type="text"], input[type="number"], textarea');
  
  inputs.forEach((input, index) => {
    if (input.value && input.id) {
      formData[input.id] = input.value;
    }
  });
  
  return formData;
}

/**
 * Save state to localStorage
 */
function saveStateToStorage(state) {
  if (!state) return;
  
  try {
    localStorage.setItem('budgley_app_state', JSON.stringify(state));
    console.log('App state saved to localStorage');
  } catch (error) {
    console.error('Failed to save app state:', error);
  }
}

/**
 * Restore app state if needed
 */
function restoreAppStateIfNeeded() {
  try {
    const savedState = localStorage.getItem('budgley_app_state');
    if (!savedState) return;
    
    const state = JSON.parse(savedState);
    const now = Date.now();
    const stateAge = now - state.timestamp;
    
    // Only restore if state is less than 5 minutes old
    if (stateAge > 5 * 60 * 1000) {
      console.log('Saved state too old, not restoring');
      localStorage.removeItem('budgley_app_state');
      return;
    }
    
    console.log('Restoring app state:', state);
    
    // Restore scroll position
    if (state.scrollPosition) {
      window.scrollTo(state.scrollPosition.x, state.scrollPosition.y);
    }
    
    // Restore form data
    if (state.formData) {
      Object.keys(state.formData).forEach(inputId => {
        const input = document.getElementById(inputId);
        if (input) {
          input.value = state.formData[inputId];
        }
      });
    }
    
    // Restore active view if different from current
    if (state.activeView && getCurrentActiveView() !== state.activeView) {
      restoreActiveView(state.activeView);
    }
    
    // Clear the saved state after restoration
    localStorage.removeItem('budgley_app_state');
    
  } catch (error) {
    console.error('Failed to restore app state:', error);
  }
}

/**
 * Restore the active view
 */
function restoreActiveView(viewId) {
  console.log('Restoring active view:', viewId);
  
  // Map view IDs to their switch functions
  const viewSwitchers = {
    'single-mode': () => window.switchToSingleView && window.switchToSingleView(),
    'comparison-mode': () => window.switchToComparisonView && window.switchToComparisonView(),
    'sankey-mode': () => window.switchToSankeyView && window.switchToSankeyView(),
    'progress-view-mode': () => window.switchToProgressView && window.switchToProgressView(),
    'expense-tracker-mode': () => window.switchToExpenseTracker && window.switchToExpenseTracker()
  };
  
  const switchFunction = viewSwitchers[viewId];
  if (switchFunction) {
    // Small delay to ensure the app is fully loaded
    setTimeout(switchFunction, 250);
  }
}

/**
 * Handle offline data sync
 */
function handleOfflineDataSync() {
  console.log('Handling offline data sync...');
  
  // Trigger any pending sync operations
  if (window.syncOfflineData && typeof window.syncOfflineData === 'function') {
    window.syncOfflineData();
  }
}

/**
 * Handle visibility change from service worker
 */
function handleVisibilityChange(data) {
  console.log('Visibility change handled:', data);
  
  if (data.visible) {
    // Page became visible
    restoreAppStateIfNeeded();
  }
}

/**
 * Trigger data sync with server
 */
function triggerDataSync() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'TRIGGER_SYNC',
      tag: 'budgley-data-sync'
    });
  }
}

/**
 * Get service worker registration status
 */
export function getServiceWorkerStatus() {
  return {
    supported: 'serviceWorker' in navigator,
    registered: !!swRegistration,
    active: swRegistration && !!swRegistration.active,
    online: isOnline,
    canInstall: !!deferredPrompt
  };
}

/**
 * Force cache update
 */
export function updateCache(urls = ['/']) {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return Promise.reject('Service Worker not available');
  }
  
  navigator.serviceWorker.controller.postMessage({
    type: 'CACHE_UPDATE',
    data: { urls }
  });
}

/**
 * Clear all caches
 */
export function clearCache() {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return Promise.reject('Service Worker not available');
  }
  
  navigator.serviceWorker.controller.postMessage({
    type: 'CLEAR_CACHE'
  });
}

console.log('Service Worker module loaded');